import { ReactNode, useState, useEffect, useId } from "react";

export interface SizeFrameProps {
  title: string;
  children: ReactNode;
  defaultSize?:
    | "half"
    | "watch"
    | "watch-wide"
    | "watch-tall"
    | "iphone"
    | "ipad";
  persistenceKey?: string;
  headerActions?: ReactNode;
}

const SIZE_DEFINITIONS = {
  half: { width: 320, height: 160, label: "Half" },
  watch: { width: 320, height: 320, label: "Watch" },
  "watch-wide": { width: 640, height: 320, label: "2x1" },
  "watch-tall": { width: 320, height: 640, label: "1x2" },
  iphone: { width: 390, height: 844, label: "iPhone" },
  ipad: { width: 1024, height: 768, label: "iPad" },
};

type SizeKey = keyof typeof SIZE_DEFINITIONS;

export function SizeFrame({
  title,
  children,
  defaultSize = "watch",
  persistenceKey,
  headerActions,
}: SizeFrameProps) {
  const componentId = useId();
  const storageKey = persistenceKey ? `${persistenceKey}-${componentId}` : null;

  // Initialize from localStorage if available, otherwise use defaultSize
  const getInitialSize = (): SizeKey => {
    if (storageKey) {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored && stored in SIZE_DEFINITIONS) {
          return stored as SizeKey;
        }
      } catch (e) {
        // localStorage not available or error - silently fall back
      }
    }
    return defaultSize;
  };

  const [selectedSize, setSelectedSize] = useState<SizeKey>(getInitialSize);
  const currentSize = SIZE_DEFINITIONS[selectedSize];

  // Persist to localStorage when size changes
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, selectedSize);
      } catch (e) {
        // localStorage not available or error - silently ignore
      }
    }
  }, [selectedSize, storageKey]);

  return (
    <>
      <h1
        style={{
          margin: 0,
          padding: "0.5rem",
          fontFamily: "Inter, system-ui, sans-serif",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: "#111827",
          textAlign: "center",
          background: "white",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ flex: 1 }}>{title}</span>
        {headerActions && <div>{headerActions}</div>}
      </h1>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "1rem",
            padding: "0.5rem 1rem",
            background: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          {(
            Object.keys(SIZE_DEFINITIONS) as Array<
              keyof typeof SIZE_DEFINITIONS
            >
          ).map((size) => (
            <label
              key={size}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                fontFamily: "Inter, system-ui, sans-serif",
                fontSize: "0.875rem",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="size"
                value={size}
                checked={selectedSize === size}
                onChange={(e) => setSelectedSize(e.target.value as SizeKey)}
                style={{ cursor: "pointer" }}
              />
              {SIZE_DEFINITIONS[size].label}
            </label>
          ))}
        </div>
        <div
          style={{
            width: `${currentSize.width}px`,
            height: `${currentSize.height}px`,
            maxWidth: "100vw",
            maxHeight: "100vh",
            overflow: "hidden",
            borderRadius: "20px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
            transition: "width 0.2s ease, height 0.2s ease",
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
}
